import {
  createContext,
  useContext,
  useState,
  useRef,
  useCallback,
  useEffect,
  ReactNode,
} from 'react';
import { useToast } from '@/hooks/use-toast';

interface RecordingSession {
  consultationId: string | null;
  patientName: string;
  selectedTemplateId: string | null;
  startedAt: string | null;
}

interface RecordingContextValue {
  // Recording state
  isRecording: boolean;
  isPaused: boolean;
  recordingTime: number;
  audioBlob: Blob | null;
  
  // Session data
  session: RecordingSession;
  
  // Recording controls
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<Blob | null>;
  pauseRecording: () => void;
  resumeRecording: () => void;
  resetRecording: () => void;
  
  // Session setters
  setSession: (session: Partial<RecordingSession>) => void;
  clearSession: () => void;
  
  // Options
  setMaxDurationSeconds: (seconds: number | undefined) => void;
  setOnLimitReached: (callback: (() => void) | undefined) => void;
  setOnWarningThreshold: (callback: ((remaining: number) => void) | undefined) => void;
}

const RecordingContext = createContext<RecordingContextValue | null>(null);

const DEFAULT_SESSION: RecordingSession = {
  consultationId: null,
  patientName: '',
  selectedTemplateId: null,
  startedAt: null,
};

const ORIGINAL_TITLE = 'ProntuÁudio';
const VISIBILITY_THROTTLE_MS = 30000; // 30 seconds

export function RecordingProvider({ children }: { children: ReactNode }) {
  const { toast } = useToast();
  
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  
  // Session state
  const [session, setSessionState] = useState<RecordingSession>(DEFAULT_SESSION);
  
  // Options refs (to avoid re-creating MediaRecorder)
  const maxDurationSecondsRef = useRef<number | undefined>(undefined);
  const onLimitReachedRef = useRef<(() => void) | undefined>(undefined);
  const onWarningThresholdRef = useRef<((remaining: number) => void) | undefined>(undefined);
  
  // MediaRecorder refs
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const warningTriggeredRef = useRef(false);
  
  // Visibility throttle
  const lastVisibilityToastRef = useRef<number>(0);

  // Timer functions
  const startTimer = useCallback(() => {
    timerRef.current = setInterval(() => {
      setRecordingTime((prev) => prev + 1);
    }, 1000);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Check for limits during recording
  useEffect(() => {
    if (!isRecording || isPaused) return;

    const maxDuration = maxDurationSecondsRef.current;
    
    // Check if we've hit the max duration
    if (maxDuration && recordingTime >= maxDuration) {
      onLimitReachedRef.current?.();
      stopRecording();
      return;
    }

    // Check for warning threshold (5 minutes before limit)
    if (maxDuration && onWarningThresholdRef.current && !warningTriggeredRef.current) {
      const remainingSeconds = maxDuration - recordingTime;
      if (remainingSeconds <= 300 && remainingSeconds > 0) {
        warningTriggeredRef.current = true;
        onWarningThresholdRef.current(remainingSeconds);
      }
    }
  }, [recordingTime, isRecording, isPaused]);

  // Dynamic tab title
  useEffect(() => {
    if (isRecording) {
      const mins = Math.floor(recordingTime / 60);
      const secs = recordingTime % 60;
      const timeStr = `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
      document.title = `⏺ Gravando (${timeStr}) — ${ORIGINAL_TITLE}`;
    } else {
      document.title = ORIGINAL_TITLE;
    }
    
    return () => {
      document.title = ORIGINAL_TITLE;
    };
  }, [isRecording, recordingTime]);

  // beforeunload protection
  useEffect(() => {
    if (!isRecording) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isRecording]);

  // visibilitychange alert with throttle
  useEffect(() => {
    if (!isRecording) return;

    const handleVisibilityChange = () => {
      if (document.hidden && isRecording) {
        const now = Date.now();
        if (now - lastVisibilityToastRef.current >= VISIBILITY_THROTTLE_MS) {
          lastVisibilityToastRef.current = now;
          toast({
            title: '⏺ Gravação em andamento',
            description: 'Você ainda está gravando uma consulta.',
          });
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isRecording, toast]);

  // Keyboard shortcut: Ctrl/Cmd + Shift + . to stop recording
  useEffect(() => {
    if (!isRecording) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === '.') {
        e.preventDefault();
        stopRecording();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isRecording]);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      warningTriggeredRef.current = false;
      lastVisibilityToastRef.current = 0;

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : 'audio/mp4',
      });

      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.start(1000);
      setIsRecording(true);
      setIsPaused(false);
      setAudioBlob(null);
      setRecordingTime(0);
      startTimer();
    } catch (err) {
      console.error('Error starting recording:', err);
      throw new Error('Não foi possível acessar o microfone. Verifique as permissões.');
    }
  }, [startTimer]);

  const stopRecording = useCallback(async (): Promise<Blob | null> => {
    return new Promise((resolve) => {
      if (!mediaRecorderRef.current) {
        resolve(null);
        return;
      }

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(chunksRef.current, {
          type: mediaRecorderRef.current?.mimeType || 'audio/webm',
        });
        setAudioBlob(blob);

        // Stop all tracks
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
          streamRef.current = null;
        }

        resolve(blob);
      };

      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
      stopTimer();
    });
  }, [stopTimer]);

  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording && !isPaused) {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      stopTimer();
    }
  }, [isRecording, isPaused, stopTimer]);

  const resumeRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording && isPaused) {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      startTimer();
    }
  }, [isRecording, isPaused, startTimer]);

  const resetRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    setIsRecording(false);
    setIsPaused(false);
    setRecordingTime(0);
    setAudioBlob(null);
    chunksRef.current = [];
    warningTriggeredRef.current = false;
    stopTimer();
  }, [isRecording, stopTimer]);

  const setSession = useCallback((partial: Partial<RecordingSession>) => {
    setSessionState((prev) => ({ ...prev, ...partial }));
  }, []);

  const clearSession = useCallback(() => {
    setSessionState(DEFAULT_SESSION);
  }, []);

  const setMaxDurationSeconds = useCallback((seconds: number | undefined) => {
    maxDurationSecondsRef.current = seconds;
  }, []);

  const setOnLimitReached = useCallback((callback: (() => void) | undefined) => {
    onLimitReachedRef.current = callback;
  }, []);

  const setOnWarningThreshold = useCallback((callback: ((remaining: number) => void) | undefined) => {
    onWarningThresholdRef.current = callback;
  }, []);

  return (
    <RecordingContext.Provider
      value={{
        isRecording,
        isPaused,
        recordingTime,
        audioBlob,
        session,
        startRecording,
        stopRecording,
        pauseRecording,
        resumeRecording,
        resetRecording,
        setSession,
        clearSession,
        setMaxDurationSeconds,
        setOnLimitReached,
        setOnWarningThreshold,
      }}
    >
      {children}
    </RecordingContext.Provider>
  );
}

export function useRecording() {
  const context = useContext(RecordingContext);
  if (!context) {
    throw new Error('useRecording must be used within a RecordingProvider');
  }
  return context;
}

export function formatRecordingTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}
